// Dev-only role override. Lets a signed-in root admin spot-check the app as
// if they were a rep / coach / org admin without logging into a separate
// account. The override only swaps the `roles` array `getServerAuth` returns
// — `useCan` and server-side `can`/`assertCan` see it uniformly. The real
// backend still enforces on the real JWT, so this only narrows what the FE
// shows; it never grants access the user doesn't actually have.

import { UserRoleEnum } from "@siro-ai/types"

export const DEV_ROLE_OVERRIDE_COOKIE = "dev-role-override"

// Ordered by tier (rep → root admin) so the dropdown reads top-to-bottom by
// access level. ROOT_SUPERADMIN is omitted — it mirrors ROOT_ADMIN and isn't
// held by any backend user yet.
const OVERRIDABLE_ROLES = [
  UserRoleEnum.REP,
  UserRoleEnum.COACH,
  UserRoleEnum.ORG_ADMIN,
  UserRoleEnum.ORG_SUPERADMIN,
  UserRoleEnum.ORG_GROUP_ADMIN,
  UserRoleEnum.ORG_GROUP_SUPERADMIN,
  UserRoleEnum.ROOT_ADMIN,
] as const

export type DevRoleOverride = (typeof OVERRIDABLE_ROLES)[number]

export const DEV_ROLE_OVERRIDE_OPTIONS: readonly DevRoleOverride[] =
  OVERRIDABLE_ROLES

const VALID = new Set<string>(OVERRIDABLE_ROLES)

export const isDevRoleOverrideAllowed = (): boolean =>
  process.env.NODE_ENV !== "production"

export const parseDevRoleOverride = (
  raw: string | undefined | null,
): DevRoleOverride | null => {
  if (!isDevRoleOverrideAllowed()) return null
  if (!raw) return null
  return VALID.has(raw) ? (raw as DevRoleOverride) : null
}
